/**
 * Outreach — the agent drafts, a person decides.
 *
 * Drafts arrive from "Draft outreach" on an opportunity. Each one can be
 * edited (recipient, subject, body) and is only sent when a person presses
 * Approve & send and confirms a dialog that says plainly a real email will go
 * out. Sent and failed messages stay in the log below.
 */
import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SectionHeader, PageState } from '@/components/weavein';
import type { FundingApi, Outreach } from '@/services/api/fundingOpportunities';
import { CARD, MUTED, ErrorNote, Pill, STATUS_LABEL, formatDate, useLoad } from './shared';

type Editable = Pick<Outreach, 'to_name' | 'to_email' | 'subject' | 'body'>;

const DraftCard: React.FC<{
  draft: Outreach;
  onSave: (patch: Partial<Editable>) => Promise<Outreach>;
  onSend: () => Promise<void>;
}> = ({ draft, onSave, onSend }) => {
  const [edit, setEdit] = useState<Partial<Editable>>({});
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const field = <K extends keyof Editable>(k: K) => (k in edit ? (edit[k] as string) : ((draft[k] as string) ?? ''));
  const dirty = Object.keys(edit).length > 0;
  const recipient = field('to_email').trim();

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(edit);
      setEdit({});
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      if (dirty) {
        await onSave(edit);
        setEdit({});
      }
      await onSend();
      setConfirming(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <li className={`${CARD} space-y-3 p-4`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[14px] font-semibold text-ink">{draft.funder_name ?? `Opportunity #${draft.opportunity_id}`}</p>
        <Pill tone="attention">{draft.drafted_by === 'agent' ? 'Drafted by the agent' : 'Drafted by your team'}</Pill>
      </div>
      {error && <ErrorNote message={error} />}
      <div className="grid gap-2 md:grid-cols-2">
        <Input value={field('to_name')} onChange={(e) => setEdit((x) => ({ ...x, to_name: e.target.value }))} placeholder="Recipient name" aria-label="Recipient name" />
        <Input value={field('to_email')} onChange={(e) => setEdit((x) => ({ ...x, to_email: e.target.value }))} placeholder="recipient@funder.org" aria-label="Recipient email" type="email" />
      </div>
      <Input value={field('subject')} onChange={(e) => setEdit((x) => ({ ...x, subject: e.target.value }))} aria-label="Subject" />
      <Textarea value={field('body')} onChange={(e) => setEdit((x) => ({ ...x, body: e.target.value }))} rows={9} aria-label="Message" />
      <div className="flex flex-wrap items-center justify-end gap-2">
        {!recipient && <span className={MUTED}>Add a recipient before sending.</span>}
        <Button size="sm" variant="outline" onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save draft'}
        </Button>
        <Button size="sm" onClick={() => setConfirming(true)} disabled={!recipient || sending}>
          <Send className="mr-1 h-3.5 w-3.5" /> Approve &amp; send
        </Button>
      </div>

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send this email?</DialogTitle>
            <DialogDescription>
              This sends a real email from your organization to {recipient}. It is recorded with your name as the person who
              approved it. It cannot be unsent.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={sending}>
              Cancel
            </Button>
            <Button onClick={send} disabled={sending}>
              {sending ? 'Sending…' : 'Send email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </li>
  );
};

const OutreachPanel: React.FC<{ api: FundingApi; onChanged: () => void }> = ({ api, onChanged }) => {
  const { data, error, loading, setData } = useLoad(() => api.listOutreach(), [api]);

  const replace = (next: Outreach) => setData((cur) => (cur ?? []).map((o) => (o.id === next.id ? { ...o, ...next } : o)));

  const drafts = (data ?? []).filter((o) => o.status === 'draft' || o.status === 'approved');
  const log = (data ?? []).filter((o) => o.status === 'sent' || o.status === 'failed');

  return (
    <div className="space-y-4">
      <div className={`${CARD} p-4`}>
        <SectionHeader title="Outreach to review" description="The agent drafts each message. Nothing is sent until a person approves it." />
        {error && <div className="mt-2"><ErrorNote message={error} /></div>}
      </div>
      {loading && !data && <PageState variant="loading" />}
      {data && drafts.length === 0 && (
        <PageState variant="empty" title="No drafts waiting" message="Draft outreach from a new funder on the Opportunities tab, then review it here." />
      )}
      <ul className="space-y-3">
        {drafts.map((d) => (
          <DraftCard
            key={d.id}
            draft={d}
            onSave={async (patch) => {
              const next = await api.updateOutreach(d.id, patch);
              replace(next);
              return next;
            }}
            onSend={async () => {
              replace(await api.sendOutreach(d.id));
              onChanged();
            }}
          />
        ))}
      </ul>

      {log.length > 0 && (
        <div className={`${CARD} p-4`}>
          <SectionHeader title="Sent" />
          <ul className="mt-2">
            {log.map((o) => (
              <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-v2border-muted py-2 last:border-0">
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-ink">{o.subject}</p>
                  <p className="text-[12px] text-ink-400">
                    {o.funder_name ? `${o.funder_name} · ` : ''}
                    {o.to_email}
                    {o.sent_at ? ` · ${formatDate(o.sent_at)}` : ''}
                    {o.error ? ` · ${o.error}` : ''}
                  </p>
                </div>
                <Pill tone={o.status === 'sent' ? 'success' : 'danger'}>{STATUS_LABEL[o.status]}</Pill>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default OutreachPanel;
